export type UserType = 'student' | 'tutor' | null;

/**
 * Get the appropriate login path based on user type or context
 * @param userType - The type of user (student/tutor)
 * @param currentPath - The current path being accessed (optional)
 * @returns The appropriate login path
 */
export const getLoginPath = (userType: UserType, currentPath?: string): string => {
  // If we have a user type, use it
  if (userType === 'tutor') {
    return '/login/tutor';
  } else if (userType === 'student') {
    return '/login/student';
  }
  
  // If no user type, try to infer from the current path
  if (currentPath) {
    const lowerPath = currentPath.toLowerCase();
    if (lowerPath.includes('tutor')) {
      return '/login/tutor';
    }
  }
  
  // Default to student login
  return '/login/student';
};

/**
 * Get redirect path after logout based on user type
 * @param userType - The type of user being logged out
 * @returns The appropriate redirect path
 */
export const getLogoutRedirectPath = (userType: UserType): string => {
  return getLoginPath(userType);
};

/**
 * Redirect to login page using window.location
 * @param userType - The type of user
 * @param currentPath - The current path being accessed (optional)
 */
export const redirectToLogin = (userType: UserType, currentPath?: string): void => {
  window.location.href = getLoginPath(userType, currentPath);
};

/**
 * Helper to determine if a path requires tutor access
 * @param path - The path to check
 * @returns true if the path is tutor-specific
 */
export const isTutorPath = (path: string): boolean => {
  const tutorPaths = [
    '/TutorDashboard',
    '/my-courses',
    '/course-edit',
    '/tutor'
  ];
  
  const lowerPath = path.toLowerCase();
  return tutorPaths.some(tutorPath => lowerPath.includes(tutorPath.toLowerCase()));
};

/**
 * Helper to determine if a path requires student access
 * @param path - The path to check
 * @returns true if the path is student-specific
 */
export const isStudentPath = (path: string): boolean => {
  const studentPaths = [
    '/StudentDashboard',
    '/class-details',
    '/student'
  ];
  
  const lowerPath = path.toLowerCase();
  return studentPaths.some(studentPath => lowerPath.includes(studentPath.toLowerCase()));
};